import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Center, Text, Flex, Switch, FormLabel } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Input, Footer } from '../';
import { isSupported } from 'twilio-video';
import { userLogin, updateSettings } from '../../redux/reducers';
import { checkSessionStatus, fetchJoinSessionData } from '../../utils/session/session';

const JoinPage = ({ history, match }) => {
  const [username, setUsername] = useState('');
  const [waitingJoin, setWaitingJoin] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const sessionId = match.params.sessionId;
  const { cameraInUse, micInUse } = useSelector((state) => state.settings);
  const [sessionStatus, setSessionStatus] = useState(null);

  useEffect(() => {
    (async () => {
      const response = await checkSessionStatus(sessionId);
      setSessionStatus(response.status || 'ERROR');
    })();
  }, [sessionId]);

  const tryJoinSession = (username) => {
    fetchJoinSessionData(username, sessionId)
      .then(() => {
        // open sessions
        history.push(`/session/${sessionId}`);
      })
      .catch((error) => {
        console.log('join failed', error, error.response, JSON.stringify(error));
        if (error.response && error.response.status == 409) {
          console.warn(`Username ${username} is already taken.`);
          setErrorMsg('join.usernameTaken');
          setWaitingJoin(false);
          return;
        }
        console.log('Cannot join session, retry after 5 seconds');
        setWaitingJoin(true);
        setTimeout(() => tryJoinSession(username), 5000);
      });
  };

  const onJoinClick = (username) => {
    // update user name
    dispatch(userLogin(username)).then(() => {
      tryJoinSession(username);
    });
  };

  const changeFormValue = (value, field) => {
    dispatch(updateSettings({ [field]: value }));
  };

  const renderLogin = ({ errorMsg }) => {
    return (
      <Flex flexDirection="column" bg="brand.10" p={10} width={400}>
        <Center>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            {t('join.join')}
          </Text>
        </Center>
        <Input
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          title={t('join.name')}
          errorBorderColor="brand.50"
          bg="brand.100"
          width="100%"
          mb={5}
        />
        {errorMsg && (
          <Center>
            <Text fontSize="l" fontWeight="bold" color="brand.50">
              {t(errorMsg)}
            </Text>
          </Center>
        )}
        <Center w="100%">
          <Button
            w="100%"
            bg="brand.50"
            color="brand.10"
            disabled={username.length === 0 || sessionId == null}
            onClick={() => onJoinClick(username)}
            mb={6}
          >
            {t('join.joinBtn')}
          </Button>
        </Center>
        <FormLabel mb={4}>{t('join.videoAudioSettings')}</FormLabel>
        <Flex alignItems="center" mb={4}>
          <Switch
            isChecked={cameraInUse}
            onChange={(e) => changeFormValue(e.target.checked, 'cameraInUse')}
            pr={3}
          />
          <Text>{cameraInUse ? t('chat.settings.cameraOn') : t('chat.settings.cameraOff')}</Text>
        </Flex>
        <Flex alignItems="center">
          <Switch
            isChecked={micInUse}
            onChange={(e) => changeFormValue(e.target.checked, 'micInUse')}
            pr={3}
          />
          <Text>{micInUse ? t('chat.settings.micOn') : t('chat.settings.micOff')}</Text>
        </Flex>
      </Flex>
    );
  };

  const renderError = () => {
    return (
      <Text color="white" mb={2}>
        {t('modal.browserNotSupported')}
      </Text>
    );
  };

  const renderWaitingJoin = () => {
    return <Text color="white">{t('waitingSessionStart')}</Text>;
  };

  const renderSessionIsOver = () => {
    return <Text color="white">{t('sessionFinished')}</Text>;
  };

  const renderSessionNotFound = () => {
    return <Text color="white">{t('sessionNotFound')}</Text>;
  };

  return (
    <Flex flexDirection="column" height="100vh" bg="primary">
      <Flex flexGrow="1" pb="32">
        <Center flexDirection="column" flexGrow="1">
          {sessionStatus === 'FINISHED' ? (
            renderSessionIsOver()
          ) : sessionStatus === 'NOT_FOUND' ? (
            renderSessionNotFound()
          ) : (
            <>
              {isSupported === false && renderError()}
              {isSupported === true && waitingJoin === false && renderLogin({ errorMsg })}
              {isSupported === true && waitingJoin === true && renderWaitingJoin()}
            </>
          )}
        </Center>
      </Flex>
      <Footer width="100%" />
    </Flex>
  );
};

JoinPage.propTypes = {
  history: PropTypes.any,
  match: PropTypes.any,
};

export default withRouter(JoinPage);
